import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import { kpiApi } from "../api/live-client.js";
import type { ManagedKpiDetail } from "../api/types.js";
import { kpiKeys } from "./kpiQueryKeys.js";

/**
 * A single configured KPI with its current target + measurement/performance
 * state (`GET /v1/kpis/:id`) — the detail drawer's source. A 404 surfaces as
 * `isError` (definitive; no retry) so the drawer shows a not-found state
 * instead of spinning.
 */
export function useKpi(kpiId: string | undefined) {
  const config = useApiClient();
  return useQuery<ManagedKpiDetail>({
    queryKey: kpiKeys.detail(kpiId ?? ""),
    queryFn: () => kpiApi.getKpi(config, kpiId!),
    enabled: Boolean(kpiId),
    staleTime: 30_000,
    // KPI detail and Hoshin may be open as separate shell/browser tabs. Always
    // cross the API boundary when this view is revisited so another surface's
    // write cannot remain hidden behind a still-fresh local cache.
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    retry: false,
  });
}
